import {ActionList, ActionMenu, IconButton, VisuallyHidden} from '@primer/react'
import {AlertIcon, CheckIcon, CopyIcon, KebabHorizontalIcon, StackRemoveIcon} from '@primer/octicons-react'
import type {RefObject} from 'react'
import type {CopyStackLinksStatus} from '../hooks/use-copy-stack-links'

type StackActionsMenuProps = {
  anchorRef: RefObject<HTMLButtonElement | null>
  copyStatus?: CopyStackLinksStatus
  onCopy?: () => void
  onMenuToggle?: () => void
  onUnstack: () => void
  unstackInactiveText?: string
}

export function StackActionsMenu({
  anchorRef,
  copyStatus,
  onCopy,
  onMenuToggle,
  onUnstack,
  unstackInactiveText,
}: StackActionsMenuProps) {
  const copyContent =
    copyStatus === 'success'
      ? {Icon: CheckIcon, label: 'Links copied!'}
      : copyStatus === 'error'
        ? {Icon: AlertIcon, label: 'Failed to copy'}
        : {Icon: CopyIcon, label: 'Copy pull request links'}
  const accessibleLabel =
    copyStatus === 'success'
      ? 'Pull request links copied'
      : copyStatus === 'error'
        ? 'Failed to copy pull request links'
        : undefined

  return (
    <ActionMenu anchorRef={anchorRef}>
      <ActionMenu.Anchor>
        <IconButton
          ref={anchorRef}
          icon={KebabHorizontalIcon}
          variant="invisible"
          aria-label="More options"
          onClick={onMenuToggle}
        />
      </ActionMenu.Anchor>
      <ActionMenu.Overlay align="end" width="small">
        <ActionList>
          {onCopy && (
            <>
              <ActionList.Item
                onSelect={event => {
                  event.preventDefault()
                  onCopy()
                }}
              >
                <ActionList.LeadingVisual>
                  <copyContent.Icon />
                </ActionList.LeadingVisual>
                <span aria-hidden={accessibleLabel ? 'true' : undefined}>{copyContent.label}</span>
                {accessibleLabel && <VisuallyHidden>{accessibleLabel}</VisuallyHidden>}
              </ActionList.Item>
              <ActionList.Divider />
            </>
          )}
          <ActionList.Item variant="danger" onSelect={onUnstack} inactiveText={unstackInactiveText}>
            <ActionList.LeadingVisual>
              <StackRemoveIcon />
            </ActionList.LeadingVisual>
            Unstack pull requests
          </ActionList.Item>
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}
